// ============================================
// useReviewContent — UKA-X01 D5
//
// Real-time Firestore listener for /tenants/hemologix/content.
// Returns sections with document IDs, grouped by category field.
// Cleans up onSnapshot listener on unmount.
// ============================================

import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import type { ClinicalReviewItem } from '../../data/portal/schema';

// Named constant — multi-tenant via auth claims is post-MVP
const TENANT_ID = 'hemologix';

/**
 * ClinicalReviewItem extended with the Firestore document ID.
 * ReviewCard requires contentId; the raw ClinicalReviewItem interface does not
 * include it, so we attach it here at the data-fetch boundary.
 */
export type ClinicalReviewItemWithId = ClinicalReviewItem & { id: string };

export interface UseReviewContentResult {
  sections: ClinicalReviewItemWithId[];
  grouped: Record<string, ClinicalReviewItemWithId[]>;
  loading: boolean;
  error: string | null;
}

export function useReviewContent(): UseReviewContentResult {
  const [sections, setSections] = useState<ClinicalReviewItemWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ref = collection(db, 'tenants', TENANT_ID, 'content');

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const items: ClinicalReviewItemWithId[] = snap.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as ClinicalReviewItem),
        }));
        setSections(items);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );

    return () => unsub();
  }, []);

  // Group by item.category (string field on ClinicalReviewItem)
  const grouped: Record<string, ClinicalReviewItemWithId[]> = {};
  for (const item of sections) {
    const key = item.category ?? 'Uncategorized';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }

  return { sections, grouped, loading, error };
}
